// M13 — Sektör Paketleri admin sayfası.
// /admin/frameworks/sector-packages
// Sol rail: paket listesi + arama + "+ Yeni Paket". Sağ panel: seçili paket editor.
// Editor S5b'de SectorPackageEditor component'i ile doldurulacak.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import type { PublishedFrameworksResponse, SectorPackageSummary } from "@mahir/shared";
import { api } from "../../api/client";
import { createSectorPackage, listSectorPackages } from "../../api/sector-packages";
import { SectorPackageEditor } from "../../components/sector-packages/SectorPackageEditor";

interface NewPackageDialogState {
  open: boolean;
  parentFrameworkVersionId: string;
  name: string;
  slug: string;
  description: string;
  submitting: boolean;
  error: string | null;
}

const initialDialog: NewPackageDialogState = {
  open: false,
  parentFrameworkVersionId: "",
  name: "",
  slug: "",
  description: "",
  submitting: false,
  error: null,
};

export function SectorPackages(): JSX.Element {
  const [packages, setPackages] = useState<SectorPackageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const [publishedFw, setPublishedFw] = useState<PublishedFrameworksResponse["frameworks"]>([]);
  const [dialog, setDialog] = useState<NewPackageDialogState>(initialDialog);

  // Liste yükle
  useEffect(() => {
    let aborted = false;
    setLoading(true);
    listSectorPackages()
      .then((res) => {
        if (!aborted) {
          setPackages(res.packages);
          if (res.packages.length > 0 && !selectedId) {
            setSelectedId(res.packages[0]!.id);
          }
        }
      })
      .catch((err: Error) => {
        if (!aborted) setError(err.message);
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [refreshTick, selectedId]);

  // Published frameworks (dialog için)
  useEffect(() => {
    api<PublishedFrameworksResponse>("/admin/frameworks/published")
      .then((res) => setPublishedFw(res.frameworks))
      .catch(() => setPublishedFw([]));
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return packages;
    return packages.filter(
      (p) =>
        p.name.toLowerCase().includes(s) ||
        p.slug.toLowerCase().includes(s) ||
        p.parentFrameworkName.toLowerCase().includes(s),
    );
  }, [packages, search]);

  const openDialog = useCallback(() => {
    setDialog({
      ...initialDialog,
      open: true,
      parentFrameworkVersionId: publishedFw[0]?.version.id ?? "",
    });
  }, [publishedFw]);

  const closeDialog = useCallback(() => setDialog(initialDialog), []);

  const submitDialog = useCallback(async () => {
    if (!dialog.parentFrameworkVersionId || !dialog.name.trim() || !dialog.slug.trim()) {
      setDialog((d) => ({ ...d, error: "Tüm zorunlu alanları doldurun" }));
      return;
    }
    setDialog((d) => ({ ...d, submitting: true, error: null }));
    try {
      const { package: pkg } = await createSectorPackage({
        parentFrameworkVersionId: dialog.parentFrameworkVersionId,
        name: dialog.name.trim(),
        slug: dialog.slug.trim(),
        ...(dialog.description.trim() ? { description: dialog.description.trim() } : {}),
      });
      setSelectedId(pkg.id);
      setRefreshTick((t) => t + 1);
      closeDialog();
    } catch (err) {
      setDialog((d) => ({
        ...d,
        submitting: false,
        error: err instanceof Error ? err.message : "Hata",
      }));
    }
  }, [dialog, closeDialog]);

  const handleRefresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)", maxWidth: 1400 }}>
      <header className="mb-6">
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.72rem",
            letterSpacing: "0.08em",
            color: "var(--color-text-3)",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          ADMİN · ÇERÇEVE YÖNETİCİSİ
        </div>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.8rem",
            color: "var(--color-text)",
          }}
        >
          Sektör Paketleri
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--color-text-2)" }}>
          Yayında bir çerçeveden sektör-özel varyant türetir. Bölüm/soru çıkarabilir, metin
          alanlarını override edebilirsiniz. Proje açılırken müşteri bu varyantı seçer.
        </p>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          gap: 24,
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          borderRadius: 12,
          overflow: "hidden",
          minHeight: 600,
        }}
      >
        {/* Sol rail */}
        <nav
          style={{
            padding: 12,
            borderRight: "1px solid var(--color-border)",
            background: "var(--color-bg)",
            overflowY: "auto",
            maxHeight: "calc(100vh - 240px)",
          }}
        >
          <button
            onClick={openDialog}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 6,
              marginBottom: 12,
              background: "var(--color-primary)",
              color: "white",
              fontSize: "0.9rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              border: "none",
            }}
          >
            <Plus size={16} /> Yeni Paket
          </button>

          <input
            type="text"
            placeholder="Paket ara…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 10px",
              border: "1px solid var(--color-border)",
              borderRadius: 6,
              fontSize: "0.85rem",
              marginBottom: 8,
            }}
          />

          {loading && (
            <div className="text-xs px-3" style={{ color: "var(--color-text-3)" }}>
              Yükleniyor…
            </div>
          )}
          {error && (
            <div className="text-xs px-3" style={{ color: "var(--color-error)" }}>
              {error}
            </div>
          )}
          {!loading && filtered.length === 0 && !error && (
            <div
              className="text-xs px-3 mt-2"
              style={{ color: "var(--color-text-3)", fontStyle: "italic" }}
            >
              Henüz paket yok
            </div>
          )}
          {filtered.map((p) => {
            const isActive = selectedId === p.id;
            const badge =
              p.status === "draft"
                ? { label: "Taslak", color: "var(--color-text-3)" }
                : p.status === "published"
                  ? { label: "Yayında", color: "var(--color-success, #2f7a3e)" }
                  : { label: "Arşivde", color: "var(--color-text-3)" };
            return (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  borderRadius: 6,
                  marginBottom: 4,
                  background: isActive ? "var(--color-surface)" : "transparent",
                  border: "1px solid",
                  borderColor: isActive ? "var(--color-primary)" : "transparent",
                  color: isActive ? "var(--color-primary)" : "var(--color-text)",
                  fontSize: "0.9rem",
                  fontWeight: isActive ? 600 : 500,
                  cursor: "pointer",
                }}
              >
                <div>{p.name}</div>
                <div
                  className="text-xs mt-0.5"
                  style={{ color: "var(--color-text-3)", fontWeight: 400 }}
                >
                  {p.parentFrameworkName} · {p.parentVersionNumber}
                </div>
                <div
                  className="text-xs mt-1"
                  style={{
                    color: badge.color,
                    fontWeight: 500,
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <span>{badge.label}</span>
                  {p.isOutdated && (
                    <span
                      style={{
                        color: "var(--color-warning, #b8860b)",
                        fontWeight: 600,
                      }}
                      title="Üst çerçeve sürümü güncellendi — yükseltme önerilir"
                    >
                      • Outdated
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </nav>

        {/* Sağ panel */}
        <main style={{ padding: 24, minWidth: 0 }}>
          {selectedId ? (
            <SectorPackageEditor
              key={selectedId}
              packageId={selectedId}
              onChanged={handleRefresh}
              onDeleted={() => {
                setSelectedId(null);
                handleRefresh();
              }}
            />
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "var(--color-text-3)",
                fontStyle: "italic",
              }}
            >
              Soldan bir paket seç veya yeni paket oluştur.
            </div>
          )}
        </main>
      </div>

      {/* Yeni paket dialog */}
      {dialog.open && (
        <NewPackageDialog
          state={dialog}
          publishedFw={publishedFw}
          onChange={(patch) => setDialog((d) => ({ ...d, ...patch }))}
          onSubmit={() => {
            void submitDialog();
          }}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}

interface DialogProps {
  state: NewPackageDialogState;
  publishedFw: PublishedFrameworksResponse["frameworks"];
  onChange: (patch: Partial<NewPackageDialogState>) => void;
  onSubmit: () => void;
  onClose: () => void;
}

function NewPackageDialog({
  state,
  publishedFw,
  onChange,
  onSubmit,
  onClose,
}: DialogProps): JSX.Element {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--color-surface)",
          borderRadius: 12,
          padding: 24,
          width: 480,
          maxWidth: "90vw",
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.3rem",
            marginBottom: 16,
          }}
        >
          Yeni Sektör Paketi
        </h2>

        <label
          style={{
            display: "block",
            fontSize: "0.85rem",
            color: "var(--color-text-2)",
            marginBottom: 4,
            fontWeight: 500,
          }}
        >
          Üst Çerçeve Sürümü *
        </label>
        <select
          value={state.parentFrameworkVersionId}
          onChange={(e) => onChange({ parentFrameworkVersionId: e.target.value })}
          style={{
            width: "100%",
            padding: "8px 10px",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            marginBottom: 12,
            fontSize: "0.9rem",
          }}
        >
          {publishedFw.length === 0 && <option value="">Yayında çerçeve yok</option>}
          {publishedFw.map((f) => (
            <option key={f.version.id} value={f.version.id}>
              {f.frameworkName} · {f.version.versionNumber}
            </option>
          ))}
        </select>

        <label
          style={{
            display: "block",
            fontSize: "0.85rem",
            color: "var(--color-text-2)",
            marginBottom: 4,
            fontWeight: 500,
          }}
        >
          Paket Adı * (örn. "TSRS · Enerji")
        </label>
        <input
          type="text"
          value={state.name}
          onChange={(e) => onChange({ name: e.target.value })}
          maxLength={200}
          style={{
            width: "100%",
            padding: "8px 10px",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            marginBottom: 12,
            fontSize: "0.9rem",
          }}
        />

        <label
          style={{
            display: "block",
            fontSize: "0.85rem",
            color: "var(--color-text-2)",
            marginBottom: 4,
            fontWeight: 500,
          }}
        >
          Slug * (URL-safe, kebab-case, örn. "tsrs-enerji")
        </label>
        <input
          type="text"
          value={state.slug}
          onChange={(e) =>
            onChange({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })
          }
          maxLength={80}
          style={{
            width: "100%",
            padding: "8px 10px",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            marginBottom: 12,
            fontSize: "0.9rem",
            fontFamily: "var(--font-mono)",
          }}
        />

        <label
          style={{
            display: "block",
            fontSize: "0.85rem",
            color: "var(--color-text-2)",
            marginBottom: 4,
            fontWeight: 500,
          }}
        >
          Açıklama (opsiyonel)
        </label>
        <textarea
          value={state.description}
          onChange={(e) => onChange({ description: e.target.value })}
          maxLength={1000}
          rows={3}
          style={{
            width: "100%",
            padding: "8px 10px",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            marginBottom: 12,
            fontSize: "0.9rem",
            fontFamily: "var(--font-sans)",
            resize: "vertical",
          }}
        />

        {state.error && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              background: "color-mix(in srgb, var(--color-error) 12%, white)",
              color: "var(--color-error)",
              fontSize: "0.85rem",
              marginBottom: 12,
            }}
          >
            {state.error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={onClose}
            disabled={state.submitting}
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              border: "1px solid var(--color-border)",
              background: "transparent",
              cursor: "pointer",
              fontSize: "0.9rem",
            }}
          >
            İptal
          </button>
          <button
            onClick={onSubmit}
            disabled={state.submitting}
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              border: "none",
              background: "var(--color-primary)",
              color: "white",
              cursor: "pointer",
              fontSize: "0.9rem",
              fontWeight: 600,
            }}
          >
            {state.submitting ? "Oluşturuluyor..." : "Oluştur"}
          </button>
        </div>
      </div>
    </div>
  );
}
